<template>
  <v-container class="fill-height d-flex align-center justify-center" fluid>
    <!-- Login блок -->
    <v-col cols="12" sm="8" md="6" lg="4" v-if="!isAuthenticated || isExpired">
      <v-card elevation="0" class="pa-8" style="min-width: 320px;">
        <v-img
            class="mx-auto"
            cover
            width="250"
            src="https://testbusiness.sagi.kz/static/media/logoBlack.c1e5e44f.png"
            alt="Logo"
        />

        <v-card-title class="text-h5 font-weight-bold mb-2">Вход Sagi</v-card-title>

        <phone-input v-model="phone" :loading="loading" />

        <v-text-field
            v-model="password"
            :disabled="loading"
            name="password"
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye-outline'"
            :type="visible ? 'text' : 'password'"
            density="comfortable"
            label="Пароль"
            placeholder="Введите пароль"
            prepend-inner-icon="mdi-lock-outline"
            variant="outlined"
            rounded="lg"
            :rules="[v => !!v || 'Пароль обязателен']"
            @click:append-inner="visible = !visible"
            class="mb-3"
        />

        <div class="text-caption mt-2 mb-2 text-center">
          Нажимая «Войти», вы принимаете
          <div class="text-caption mt-2 mb-2">
            <v-btn
                variant="text"
                size="x-small"
                href="https://sagi.kz/terms.html"
                prepend-icon="mdi-open-in-new"
                target="_blank"
                class="ml-1 text-none"
            >
              пользовательское соглашение
            </v-btn>
            и
            <v-btn
                variant="text"
                size="x-small"
                href="https://docs.google.com/document/d/15x1I66SyyUeaji6V91RhQydDAVJ9-YONZw5E8-xm5MI/edit?tab=t.0#heading=h.qitbtswspvwu"
                prepend-icon="mdi-open-in-new"
                target="_blank"
                class="ml-2 text-none"
            >
              политику конфиденциальности
            </v-btn>
          </div>
        </div>

        <v-btn
            block
            variant="tonal"
            class="mt-2 mb-2"
            rounded="lg"
            size="large"
            :disabled="!phone || !password || loading"
            :loading="loading"
            @click="submit"
        >
          Войти
        </v-btn>

        <v-alert
            v-if="alertState"
            :type="alertState === 'success' ? 'success' : 'error'"
            :text="
            alertState === 'success'
              ? 'Вы успешно авторизовались'
              : 'Ошибка авторизации: Неверный номер телефона или пароль'
          "
            closable
            rounded="lg"
            variant="tonal"
            :icon="
            alertState === 'success'
              ? 'mdi-check-circle-outline'
              : 'mdi-alert-circle-outline'
          "
            class="pt-2 pb-2"
        />

        <div class="text-center mb-2 mt-2">
          Не получается войти?
          <a href="https://business.sagi.kz/auth">Восстановите пароль.</a>
        </div>

        <div class="text-center">
          Нет аккаунта? Зарегистрируйтесь в системе
          <a href="https://sagi-app.kz">Sagi</a>
        </div>
      </v-card>
    </v-col>

    <!-- Промо-блок после логина -->
    <v-col v-else class="text-center pa-0">
      <LoyaltyInfo />
    </v-col>

    <!-- Диалоговое окно об истечении contextKey -->
    <v-dialog v-model="showExpiryWarning" width="400">
      <v-card>
        <v-card-title class="text-h6">Сессия истекает</v-card-title>
        <v-card-text>
          Ключ сессии МойСклад скоро истечёт. Страница автоматически обновится через
          {{ countdown }} секунд.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" @click="refreshPage">Обновить сейчас</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import LoyaltyInfo from "@/components/LoyaltyInfo.vue"
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import axios from "axios"
import { useUserStore } from "@/stores/user"
import PhoneInput from "./PhoneInput.vue"

const userStore = useUserStore()
const isAuthenticated = computed(() => userStore.isAuthenticated)
const isExpired = computed(() => userStore.isExpired)

const phone = ref("+7")
const password = ref("")
const visible = ref(false)
const loading = ref(false)
const alertState = ref(null)

// предупреждение об истечении contextKey
const showExpiryWarning = ref(false)
const countdown = ref(30)
const reloadTimeout = ref(null)
const warningTimeout = ref(null)
const countdownInterval = ref(null)

const handleBeforeUnload = () => {
  userStore.clearUserData()
  sessionStorage.clear()
}

onMounted(() => {
  if (isAuthenticated.value && isExpired.value) {
    userStore.clearUserData()
    sessionStorage.clear()
  }
  window.addEventListener("beforeunload", handleBeforeUnload)

  // Через 4.5 минуты показываем предупреждение
  warningTimeout.value = setTimeout(() => {
    showExpiryWarning.value = true
    startCountdown()
  }, 4.5 * 60 * 1000)
})

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleBeforeUnload)
  clearTimeout(reloadTimeout.value)
  clearTimeout(warningTimeout.value)
  clearInterval(countdownInterval.value)
})

const startCountdown = () => {
  countdown.value = 30
  countdownInterval.value = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownInterval.value)
      refreshPage()
    }
  }, 1000)
}

const refreshPage = () => {
  showExpiryWarning.value = false
  window.location.reload()
}

const getQueryParam = (name) => {
  const params = new URLSearchParams(window.location.search)
  return params.get(name)
}

const submit = async () => {
  loading.value = true
  alertState.value = null
  try {
    const contextKey = getQueryParam("contextKey")
    const authResponse = await axios.post(
        "https://api.dev.sagisoft.xyz/sagi/api/authorize",
        {
          phone: "+".concat(phone.value),
          password: password.value,
          contextKey,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
    )

    userStore.setUserData(authResponse.data)
    alertState.value = "success"

    phone.value = "+7"
    password.value = ""

    setTimeout(() => {
      alertState.value = null
    }, 2000)
  } catch (err) {
    console.error(err)
    alertState.value = "error"
  } finally {
    loading.value = false
  }
}
</script>
